import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import i18n from 'i18next';
import { isEmpty, isNil } from 'lodash-es';
import { StrictMode } from 'react';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { initReactI18next } from 'react-i18next';
import { persistStore } from 'redux-persist';
import { onCLS, onINP, onLCP } from 'web-vitals';
import enStaticLocales from '../assets/locales/en';
import itStaticLocales from '../assets/locales/it';
import { setupApp } from '../constants/EnvironmentSetup';
import type {
  AppConfigType,
  AppConfigurationType,
} from '../redux/ducks/ConfigDucks.types';
import store from '../redux/stores/GlobalStore';
import type { AppSsrFetchedDataType } from '../server/AppSsr';
import { getEnvironmentVariable } from '../utils/EnvironmentHelper/functions';
import Entrypoint from './Entrypoint';
// Required css styles
// BEGIN
import '../index.css';
import '@fontsource-variable/nunito';
import '@fontsource-variable/nunito/wght.css';
import '@fontsource-variable/nunito/wght-italic.css';
// import 'tw-animate-css';
// END

const glob = typeof window !== 'undefined' ? window : import.meta.env;
const encodedConfig = glob?.__app_runtime_environment;
if (!encodedConfig || isEmpty(encodedConfig)) {
  throw new Error('Config not found');
}

// Initial setup: TODO can be optimized for env vars onli, the config is inherited already
const appConfig: AppConfigType = JSON.parse(encodedConfig.REACT_APP_CONFIG);
setupApp(appConfig);

const ssrFetchedData: AppSsrFetchedDataType = encodedConfig.SSR_FETCHED_DATA;

const appConfiguration: AppConfigurationType = ssrFetchedData.appConfiguration;

const location: string = window.location.pathname;

const appVersion = getEnvironmentVariable('VITE_APP_VERSION');

Bugsnag.start({
  apiKey: getEnvironmentVariable('REACT_APP_BUGSNAG_API_KEY') || 'bogus_key',
  plugins: [new BugsnagPluginReact()],
  generateAnonymousId: false,
  autoTrackSessions: false,
  collectUserIp: false,
  appVersion: appVersion || '0.0.0',
  appType: 'browser',
  releaseStage: import.meta.env?.PROD ? 'production' : 'staging',
});

const BugsnagReactPlugin = Bugsnag.getPlugin('react');

const ErrorBoundary = !isNil(BugsnagReactPlugin)
  ? BugsnagReactPlugin.createErrorBoundary(React)
  : undefined;

i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  //.use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    debug: false,
    fallbackLng: ssrFetchedData.language,
    lng: ssrFetchedData.language,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    react: {
      useSuspense: false,
    },
    resources: {
      en: {
        translation: {
          static: {
            ...enStaticLocales,
          },
          dynamic: {},
        },
      },
      it: {
        translation: {
          static: {
            ...itStaticLocales,
          },
          dynamic: {},
        },
      },
    },
  });

for (const locale of appConfiguration.locales) {
  for (
    let localeIndex = 0;
    localeIndex < locale.locales.length;
    localeIndex++
  ) {
    const currentLocale = locale.locales[localeIndex];

    let existingBundle = i18n.getResourceBundle(
      currentLocale.language,
      'translation',
    );
    if (isNil(existingBundle)) {
      existingBundle = {
        dynamic: {},
      };
    }

    existingBundle.dynamic[locale.id] = currentLocale.locale;

    i18n.addResourceBundle(
      currentLocale.language,
      'translation',
      existingBundle,
    );
  }
}

const persistor = persistStore(store);
const isOnClient: boolean = true;

const onCaughtErrorCallback = (e: any, errorInfo: any) => {
  console.error('Hydration caught error', e, errorInfo);
};

const onUncaughtError = (e: any, errorInfo: any) => {
  console.error('Hydration uncaught error', e, errorInfo);
};

persistor.subscribe(() => {
  /* Hydrate React components when persistor has synced with redux store */
  const { bootstrapped } = persistor.getState();

  if (bootstrapped) {
    const root = document.getElementById('root');
    if (!root) {
      throw new Error('Root element not found');
    }

    if (!isNil(ErrorBoundary)) {
      hydrateRoot(
        root,
        <StrictMode>
          <ErrorBoundary>
            <Entrypoint
              ssrFetchedData={ssrFetchedData}
              location={location}
              onClient={isOnClient}
            />
          </ErrorBoundary>
        </StrictMode>,
        {
          onCaughtError: onCaughtErrorCallback,
          onUncaughtError: onUncaughtError,
        },
      );
    } else {
      hydrateRoot(
        root,
        <StrictMode>
          <Entrypoint
            ssrFetchedData={ssrFetchedData}
            location={location}
            onClient={isOnClient}
          />
        </StrictMode>,
        {
          onCaughtError: onCaughtErrorCallback,
          onUncaughtError: onUncaughtError,
        },
      );
    }
  }
});

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', {
        scope: '/',
      })
      .then((registration) => {
        // Registration was successful
        console.log(
          'Main service worker registration successful with scope: ',
          registration.scope,
        );
      })
      .catch((err) => {
        // registration failed :(
        console.log('Main service worker registration failed: ', err);
      });
    navigator.serviceWorker
      .register('/push/onesignal/OneSignalSDKWorker.js', {
        scope: '/push/onesignal/',
      })
      .then((registration) => {
        // Registration was successful
        console.log(
          'Push notification service worker registration successful with scope: ',
          registration.scope,
        );
      })
      .catch((err) => {
        // registration failed :(
        console.log(
          'Push notification service worker registration failed: ',
          err,
        );
      });
  });
}

onCLS(console.log);
onINP(console.log);
onLCP(console.log);
